/**
 * Phase 5D — Donation page (AUD-first presets, USD checkout).
 *
 * - AUD presets: 10, 25, 50, 100.
 * - Display currency selector: AUD / USD / KES.
 * - Backend always charges in USD via /api/payments/paystack/initialize.
 * - No Paystack/USD-only wording in public-facing copy.
 */
import React, { useMemo, useState } from 'react';
import { Loader2, Heart, ShieldCheck } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import FloatingActionButtons from '@/components/shared/FloatingActionButtons';
import Seo from '@/components/shared/Seo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const PRESET_AUD = [10, 25, 50, 100];
const CURRENCIES = [
  { code: 'AUD', label: 'AUD' },
  { code: 'USD', label: 'USD' },
  { code: 'KES', label: 'KES' },
];

const DonatePage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(25);
  const [customAmount, setCustomAmount] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [displayCurrency, setDisplayCurrency] = useState('AUD');
  const [pricing, setPricing] = useState(null);

  React.useEffect(() => {
    fetch(`${BACKEND_URL}/api/public/pricing`).then((r) => r.json())
      .then((d) => setPricing(d))
      .catch(() => {});
  }, []);

  const fxUsd = pricing?.fx_aud_to_usd ?? 0.65;
  const fxKes = pricing?.fx_aud_to_kes ?? 86.5;

  // Canonical donation amount lives in AUD now.
  const amountAud = useMemo(() => {
    if (selectedPreset === 'custom') {
      const n = Number(customAmount);
      return Number.isFinite(n) && n > 0 ? n : 0;
    }
    return selectedPreset || 0;
  }, [selectedPreset, customAmount]);

  const checkoutUsd = Math.round(amountAud * fxUsd * 100) / 100;
  const displayed = useMemo(() => {
    if (displayCurrency === 'AUD') return amountAud;
    if (displayCurrency === 'USD') return checkoutUsd;
    return Math.round(amountAud * fxKes * 100) / 100;
  }, [displayCurrency, amountAud, checkoutUsd, fxKes]);

  const handleDonate = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error('Please fill in your name and email.');
      return;
    }
    if (amountAud <= 0) {
      toast.error('Please choose or enter a donation amount.');
      return;
    }
    try {
      setSubmitting(true);
      const res = await fetch(`${BACKEND_URL}/api/payments/paystack/initialize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          amount: amountAud,                       // AUD source amount
          student_name: name.trim(),
          phone: null,
          payment_type: 'donation',
          selected_payment_currency: displayCurrency,
          metadata: message ? { donor_message: message.trim() } : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.authorization_url) {
        toast.error(data?.detail || 'Could not start donation. Please try again.');
        return;
      }
      window.location.href = data.authorization_url;
    } catch (_err) {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Seo title="Support a Child's Education | Donate · Winhouse Tutors"
        description="Every donation helps provide learning resources to students who need them most."
        canonical="/donate" />
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      <FloatingActionButtons />

      <main className="flex-1">
        <section className="bg-black text-white py-12 sm:py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-xs uppercase tracking-wide mb-4">
              <Heart className="h-3.5 w-3.5 text-[#D3D86F]" />
              Support · Education Access
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3">Support a Child&apos;s Education</h1>
            <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto">
              Every donation helps provide learning resources to students who need them most.
            </p>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-4 py-10 w-full">
          <Card>
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleDonate} className="space-y-6" data-testid="donate-form">
                <div>
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <Label className="mb-0">Choose an amount (AUD)</Label>
                    <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-0.5 text-xs" data-testid="donate-currency-pills">
                      {CURRENCIES.map((c) => (
                        <button key={c.code} type="button" onClick={() => setDisplayCurrency(c.code)}
                          data-testid={`donate-currency-${c.code}`}
                          className={`px-3 py-1 rounded-full transition-colors ${displayCurrency === c.code ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}>
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {PRESET_AUD.map((n) => (
                      <button type="button" key={n} onClick={() => { setSelectedPreset(n); setCustomAmount(''); }}
                        className={`py-3 rounded-lg border-2 font-semibold text-lg transition-colors ${selectedPreset === n ? 'border-[#007A7A] bg-[#E6F4F4] text-[#007A7A]' : 'border-gray-200 hover:border-gray-300 text-gray-700'}`}
                        data-testid={`donate-preset-${n}`}>
                        AUD {n}
                      </button>
                    ))}
                    <button type="button" onClick={() => setSelectedPreset('custom')}
                      className={`py-3 rounded-lg border-2 font-semibold transition-colors ${selectedPreset === 'custom' ? 'border-[#007A7A] bg-[#E6F4F4] text-[#007A7A]' : 'border-gray-200 hover:border-gray-300 text-gray-700'}`}
                      data-testid="donate-preset-custom">Custom</button>
                  </div>
                  {selectedPreset === 'custom' && (
                    <div className="mt-3">
                      <Label htmlFor="custom-amount" className="text-sm">Custom amount (AUD)</Label>
                      <Input id="custom-amount" type="number" min="1" step="0.01" value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)} placeholder="e.g. 100" data-testid="donate-custom-amount" />
                    </div>
                  )}
                </div>

                {amountAud > 0 && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs space-y-1.5" data-testid="donate-breakdown">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Donation Amount</span>
                      <span className="font-semibold text-gray-900" data-testid="donate-aud-amount">AUD {amountAud.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Selected Currency</span>
                      <span className="font-semibold text-gray-900">
                        {displayCurrency} {displayed.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-gray-200 pt-1.5">
                      <span className="text-gray-600">Checkout Amount</span>
                      <span className="font-semibold text-emerald-700" data-testid="donate-usd-checkout">USD {checkoutUsd.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="donor-name">Your name</Label>
                    <Input id="donor-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" required data-testid="donate-name" />
                  </div>
                  <div>
                    <Label htmlFor="donor-email">Email</Label>
                    <Input id="donor-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="donate-email" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="donor-msg">Message (optional)</Label>
                  <Textarea id="donor-msg" rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
                    placeholder="A note for the team (optional)" data-testid="donate-message" />
                </div>

                <Button type="submit" disabled={submitting || amountAud <= 0}
                  className="w-full gap-2 py-6 text-base bg-[#007A7A] hover:bg-[#005f5f] text-white" data-testid="donate-submit">
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 animate-spin" />Processing…</>
                  ) : (
                    <><Heart className="h-4 w-4" />Donate Securely · {displayCurrency} {displayed.toFixed(2)}</>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-2 text-[11px] text-gray-500">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Your payment is processed securely. Winhouse Tutors is not a registered charity — donations are not tax-deductible.
                </div>
              </form>
            </CardContent>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DonatePage;
